import { Box, Checkbox, FormControlLabel, IconButton, List, ListItem, Paper, TextField } from "@material-ui/core"
import { useEffect, useMemo, useState } from "react"
import type { ChangeEvent, ReactElement } from "react"
import InfiniteScroll from "react-infinite-scroll-component"
import { connect } from "react-redux"
import { useCallbackSafeProps } from "../../../../hooks"
import type { PagingActions, PagingOptions } from "../../../../models"
import {
  comaSetFilter,
  fetchAllLicences,
  topographyChangePage,
  topographyDelete,
  topographyFetchList,
  topographyLoad,
  topographySetCoordsFilter
} from "../../../../redux"
import type {
  AppState,
  AppThunkDispatch,
  CoMaFilterAction,
  Licence,
  Topography,
  TopographyActions
} from "../../../../redux"
import { Body1, LoadingContainer } from "../../../atoms"
import type { OptionalIdTopo } from "../../../organisms"
import { TopographicForm } from "../../../organisms"

import "./topography-page.styles.scss"
import Add from "@material-ui/icons/Add"
import Check from "@material-ui/icons/Check"
import { useTranslation } from "react-i18next"
import { BehaviorSubject } from "rxjs"
import type { Subscription } from "rxjs"
import { debounceTime, skip } from "rxjs/operators"
import { ConfirmationDialog } from "../../../molecules"
import type { IConfirmationDialogProps } from "../../../molecules"

export interface ITopographyPageStateProps {
  list: Topography[]
  selectedTopography?: Topography
  filterValue: string
  paging: PagingOptions
  licences?: Licence[]
  onlyWithoutCoords: boolean
  fetchList: () => void
  fetchLicences: () => void
  setSelected: (topography?: Topography) => void
  changePage: (page: number) => void
  setFilter: (value: string) => void
  deleteTopo: (id: string) => void
  setCoordsFilter: (value: boolean) => void
}

function TopographyPageView(props: ITopographyPageStateProps): ReactElement {
  const {
    list,
    selectedTopography,
    filterValue,
    paging,
    licences,
    onlyWithoutCoords,
    fetchLicences,
    fetchList,
    changePage,
    setFilter,
    deleteTopo,
    setSelected,
    setCoordsFilter
  } = props
  const { t } = useTranslation("contentManager")
  const searchDelay: number = 1000
  const hasMorePages: boolean = useMemo<boolean>(() => {
    return paging.currentPage + 1 !== paging.totalPages
  }, [paging])
  const [getSafePaging] = useCallbackSafeProps(paging)
  const [confirmDialogState, setConfirmDialogState] = useState<IConfirmationDialogProps>({
    open: false,
    title: t("topography.deleteDialog.deleteTopography"),
    bodyText: t("topography.deleteDialog.confirmDelete"),
    confirmText: t("topography.deleteDialog.deleteText"),
    onCancel: closeConfirm,
    onConfirm: () => {}
  })

  const [searchValue, setSearchValue] = useState<string>("")

  const [searchSubscription, setSearchSubscription] = useState<Subscription>()

  const [searchSubject] = useState<BehaviorSubject<string>>(new BehaviorSubject<string>(searchValue))
  // biome-ignore lint/correctness/useExhaustiveDependencies: TODO in a later PR
  useEffect(() => {
    fetchLicences()
    setSearchSubscription(
      searchSubject.pipe(skip(1), debounceTime(searchDelay)).subscribe((value: string) => {
        setFilter(value)
      })
    )
    return cleanUp
  }, [])
  // biome-ignore lint/correctness/useExhaustiveDependencies: TODO in a later PR
  useEffect(() => {
    fetchList()
  }, [filterValue, onlyWithoutCoords])

  function cleanUp(): void {
    if (searchSubscription) {
      searchSubscription.unsubscribe()
    }
  }

  function onFilterValueChanged(event: ChangeEvent<HTMLInputElement>): void {
    const { value } = event.target
    setSearchValue(value)
    searchSubject.next(value)
  }

  function selectTopography(topography: OptionalIdTopo): () => void {
    return () => {
      setSelected(topography)
    }
  }

  function nextPage(): void {
    if (!hasMorePages) return
    changePage(getSafePaging().currentPage + 1)
  }

  function createEmptyTopography(): OptionalIdTopo {
    return {
      name: "",
      page: 0,
      letter: "",
      lat: "",
      lng: "",
      verified: false
    }
  }

  function closeConfirm(): void {
    setConfirmDialogState({
      ...confirmDialogState,
      open: false
    })
  }

  function deleteTopography(id: string): void {
    setConfirmDialogState({
      ...confirmDialogState,
      open: true,
      onConfirm: () => {
        deleteTopo(id)
        closeConfirm()
        setSelected(undefined)
      }
    })
  }

  return (
    <Box className="topography-page">
      <Paper className="topography-list-wrapper" square elevation={3}>
        <FormControlLabel
          control={
            <Checkbox checked={onlyWithoutCoords} color="primary" onClick={() => setCoordsFilter(!onlyWithoutCoords)} />
          }
          label={t("topography.hideToponyms")}
          className="topo-list-filter-checkbox"
        />
        <Box className="topographic-search-field">
          <TextField
            variant="outlined"
            placeholder={t("topography.search")}
            value={searchValue ?? ""}
            onChange={onFilterValueChanged}
          />
          <IconButton className="add-topography-button" onClick={selectTopography(createEmptyTopography())}>
            <Add />
          </IconButton>
        </Box>
        <Box id="topography-scroll-list" className="topography-list">
          <List>
            <InfiniteScroll
              dataLength={list.length}
              next={nextPage}
              hasMore={hasMorePages}
              loader={
                <LoadingContainer sections={["topography", "licences"]}>
                  <Box className="topo-no-results">{t("topography.noResultsFound")}</Box>
                </LoadingContainer>
              }
              hasChildren={list.length !== 0}
              scrollableTarget="topography-scroll-list"
            >
              {list.map((topography: Topography) => (
                <ListItem
                  className="topography-list-item"
                  key={topography.id}
                  button
                  onClick={selectTopography(topography)}
                  disabled={searchValue !== filterValue}
                >
                  <Body1>
                    {topography.name} {topography.suffix ? topography.suffix : ""}
                  </Body1>
                  {topography.verified && <Check className="item-verified" />}
                </ListItem>
              ))}
            </InfiniteScroll>
          </List>
        </Box>
      </Paper>
      <Box className="topography-content-wrapper">
        {selectedTopography && licences && (
          <TopographicForm topography={selectedTopography} onDelete={deleteTopography} licences={licences} />
        )}
      </Box>
      <ConfirmationDialog {...confirmDialogState} />
    </Box>
  )
}

function mapStateToProps(state: AppState) {
  return {
    list: state.topography.list,
    selectedTopography: state.topography.item,
    filterValue: state.contentManager.filter.value,
    paging: state.topography.pagingOptions,
    licences: state.licences.licences,
    onlyWithoutCoords: state.topography.coordsFilter
  }
}

function mapDispatchToProps(dispatch: AppThunkDispatch<TopographyActions | CoMaFilterAction | PagingActions>) {
  return {
    fetchList: () => dispatch(topographyFetchList()),
    fetchLicences: () => dispatch(fetchAllLicences()),
    setSelected: (topography?: Topography) => dispatch(topographyLoad(topography)),
    changePage: (page: number) => dispatch(topographyChangePage(page)),
    setFilter: (value: string) => dispatch(comaSetFilter("name", value)),
    deleteTopo: (id: string) => dispatch(topographyDelete(id)),
    setCoordsFilter: (value: boolean) => dispatch(topographySetCoordsFilter(value))
  }
}

export const TopographyPage = connect(mapStateToProps, mapDispatchToProps)(TopographyPageView)
